import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import Button from '@components/dataEntry/button/Button';
import { IgalleryItem } from '@typings/pages/gallery.interface';
import { PAGE_LAYOUT_TYPE } from '@typings/pages/pageLayout.interface';

const YearBlock = styled.div`
  margin-bottom: 32px;
`;

const YearTitle = styled.h3`
  font-weight: 800;
  margin-bottom: 12px;
`;

const MonthGrid = styled.div<{
  line: number;
  mobileLine?: number;
}>`
  display: grid;
  grid-template-columns: repeat(${({ line }) => line || 4}, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 480px) {
    grid-template-columns: repeat(
      ${({ mobileLine }) => mobileLine || 2},
      minmax(0, 1fr)
    );
  }
`;

const MonthCard = styled.button`
  width: 100%;
  aspect-ratio: 3 / 4;
  border-radius: 12px;
  border: 1px solid var(--lineColor);
  overflow: hidden;
  position: relative;
  background: #ccc;
  cursor: var(--hoverCursorURL), pointer;
  padding: 0;
  transition: transform 0.6s ease;
  transform-style: preserve-3d;
  &.flipped {
    transform: rotateY(180deg);
  }

  &:hover {
    outline: 2px solid var(--primaryColor);
  }
`;

const MonthThumb = styled.div<{ src?: string }>`
  width: 100%;
  height: 100%;
  background: ${({ src }) =>
    src ? `center / cover no-repeat url(${src})` : '#b3b3b3'};
`;

const MonthLabel = styled.div`
  position: absolute;
  bottom: 8px;
  left: 8px;
  right: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #fff;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.6);
  font-weight: 800;
  font-size: 16px;
`;

type Props = {
  items: IgalleryItem[];
  isEdit?: boolean;
  collectionPath: string;
  line: number;
  mobileLine?: number;
  onSelectMonth: (params: {
    year: number;
    month: number;
    list: IgalleryItem[];
  }) => void;
};

function YearlyGalleryView({
  items,
  isEdit,
  collectionPath,
  line,
  mobileLine,
  onSelectMonth,
}: Props) {
  const [visibleYears, setVisibleYears] = useState(2);
  const [flippedKey, setFlippedKey] = useState<string | null>(null);

  const grouped = useMemo(() => {
    const map = new Map<number, Map<number, IgalleryItem[]>>();
    items.forEach((item) => {
      const dateObj: Date =
        (item as any)?.date?.toDate?.() ||
        new Date(
          (item as any)?.date?._seconds
            ? (item as any).date._seconds * 1000
            : item?.date,
        );
      const year = dateObj.getFullYear();
      const month = dateObj.getMonth() + 1;
      if (!map.has(year)) map.set(year, new Map());
      const m = map.get(year)!;
      if (!m.has(month)) m.set(month, []);
      m.get(month)!.push(item);
    });
    return Array.from(map.entries())
      .sort((a, b) => b[0] - a[0])
      .map(([year, monthMap]) => ({
        year,
        months: Array.from(monthMap.entries()).sort((a, b) => a[0] - b[0]),
      }));
  }, [items]);

  const getThumb = (list: IgalleryItem[]) =>
    list?.[0]?.imgURL || list?.[0]?.imgURLList?.[0] || '';

  const limited = grouped.slice(0, visibleYears);
  const hasMore = grouped.length > visibleYears;

  return (
    <>
      {limited.map(({ year, months }) => (
        <YearBlock key={year}>
          <YearTitle>{year}</YearTitle>
          <MonthGrid line={line || 4} mobileLine={mobileLine}>
            {Array.from({ length: 12 }, (_, idx) => idx + 1).map((m) => {
              const found = months.find(([month]) => month === m);
              const monthList = found ? found[1] : [];
              const thumb =
                monthList.length > 0 ? getThumb(monthList) : undefined;
              const cardKey = `${year}-${m}`;
              return (
                <MonthCard
                  key={cardKey}
                  className={flippedKey === cardKey ? 'flipped' : ''}
                  onClick={() => {
                    if (monthList.length === 0) return;
                    setFlippedKey(cardKey);
                    setTimeout(() => setFlippedKey(null), 600);
                    onSelectMonth({ year, month: m, list: monthList });
                  }}
                  disabled={monthList.length === 0}
                  style={{ opacity: monthList.length === 0 ? 0.35 : 1 }}
                >
                  <MonthThumb src={thumb} />
                  <MonthLabel>
                    <h6>{m.toString().padStart(2, '0')}</h6>
                    <span>{monthList.length}</span>
                  </MonthLabel>
                </MonthCard>
              );
            })}
          </MonthGrid>
        </YearBlock>
      ))}

      {hasMore && (
        <div style={{ margin: '8px 0 24px' }}>
          <Button shape="stroke" onClick={() => setVisibleYears((v) => v + 2)}>
            더 보기 (나머지 {grouped.length - visibleYears}년)
          </Button>
        </div>
      )}
    </>
  );
}

export default YearlyGalleryView;
